import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Building2, Users2, Book } from 'lucide-react';

const API_URL = '';

export default function AboutPage({ user, onLogout, theme, onThemeToggle }) {
  const [profile, setProfile] = useState(null);
  const [directory, setDirectory] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({ description: '', mission: '' });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = () => {
    axios.get(`${API_URL}/api/company-profile`).then(res => {
      setProfile(res.data);
      if (res.data) setFormData({ description: res.data.description, mission: res.data.mission });
    });
    axios.get(`${API_URL}/api/directory`).then(res => setDirectory(res.data));
    axios.get(`${API_URL}/api/users`).then(res => setDirectory(res.data));
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/company-profile`, formData, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
      });
      toast.success('Company profile updated!');
      setEditMode(false);
      loadData();
    } catch (error) {
      toast.error('Failed to update profile');
    }
  };

  const canEdit = ['admin', 'principal'].includes(user.role);

  return (
    <Layout user={user} onLogout={onLogout} theme={theme} onThemeToggle={onThemeToggle}>
      <div className="p-8 space-y-8" data-testid="about-page">
        <div>
          <h1 className="text-4xl font-bold text-foreground mb-2">About Desq</h1>
          <p className="text-muted-foreground">Company information, team directory, and organizational structure</p>
        </div>

        <div className="grid gap-6">
          <Card className="glass-effect border-border/40" data-testid="company-profile-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Building2 className="w-6 h-6 text-primary" />
                  <CardTitle>Company Profile</CardTitle>
                </div>
                {canEdit && !editMode && (
                  <Button size="sm" onClick={() => setEditMode(true)} data-testid="btn-edit-profile">
                    Edit Profile
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {editMode && canEdit ? (
                <form onSubmit={handleSaveProfile} className="space-y-4">
                  <div>
                    <Label htmlFor="description">Company Description</Label>
                    <Textarea
                      id="description"
                      data-testid="input-description"
                      rows={6}
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Describe the company's history, design philosophy, and values..."
                    />
                  </div>
                  <div>
                    <Label htmlFor="mission">Mission Statement</Label>
                    <Textarea
                      id="mission"
                      data-testid="input-mission"
                      rows={4}
                      value={formData.mission}
                      onChange={(e) => setFormData({ ...formData, mission: e.target.value })}
                      placeholder="Our mission is to..."
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" data-testid="btn-save-profile">Save Changes</Button>
                    <Button type="button" variant="outline" onClick={() => setEditMode(false)} data-testid="btn-cancel-edit">Cancel</Button>
                  </div>
                </form>
              ) : (
                <div className="space-y-4">
                  {profile ? (
                    <>
                      <div>
                        <h3 className="font-semibold text-foreground mb-2">About Us</h3>
                        <p className="text-muted-foreground text-sm leading-relaxed">{profile.description}</p>
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground mb-2">Mission</h3>
                        <p className="text-muted-foreground text-sm leading-relaxed">{profile.mission}</p>
                      </div>
                    </>
                  ) : (
                    <p className="text-muted-foreground text-sm">No company profile available. {canEdit && 'Click Edit to add one.'}</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="glass-effect border-border/40" data-testid="directory-card">
            <CardHeader>
              <div className="flex items-center gap-3">
                <Users2 className="w-6 h-6 text-primary" />
                <CardTitle>Company Directory</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {directory.length > 0 ? (
                  directory.map((emp) => (
                    <div key={emp.id} className="flex items-center justify-between p-4 bg-muted/30 rounded-md" data-testid={`directory-entry-${emp.id}`}>
                      <div className="flex-1">
                        <p className="font-medium text-foreground">{emp.full_name}</p>
                        <p className="text-xs text-muted-foreground capitalize">{emp.role} · {emp.department}</p>
                      </div>
                      <div className="text-right space-y-1">
                        <p className="text-sm text-muted-foreground">{emp.email}</p>
                        {emp.phone_number && (
                          <p className="text-sm text-primary">{emp.phone_number}</p>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-muted-foreground text-sm">No employees found</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}